/*
 * Назначение: второй шаг входа — код подтверждения из письма.
 * Функционал: ввод кода, галочка «не спрашивать код на этом устройстве»
 * (показывается только если срок доверия задан в настройках), возврат к вводу
 * пароля. Подсказка с маской адреса приходит с сервера — адрес целиком не
 * показываем.
 */

import { useState } from "react";
import { AlertCircle, Loader2, ShieldCheck } from "lucide-react";

import { ApiError } from "../../lib/api";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function CodeForm({
  emailHint,
  ttlMinutes,
  canRemember,
  onConfirm,
  onBack,
}: {
  emailHint: string | null;
  ttlMinutes: number | null;
  canRemember: boolean;
  onConfirm: (code: string, rememberDevice: boolean) => Promise<void>;
  onBack: () => void;
}) {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Введи код из письма");
      return;
    }

    setLoading(true);
    try {
      await onConfirm(trimmed, remember);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail || "Код не подошёл");
      } else {
        setError("Сервер недоступен. Проверь соединение");
      }
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          Код отправлен на {emailHint || "вашу почту"}
          {ttlMinutes ? ` и действует ${ttlMinutes} мин` : ""}.
        </span>
      </div>

      <div className="space-y-2">
        <Label htmlFor="code">Код из письма</Label>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
          autoFocus
        />
      </div>

      {canRemember && (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={remember}
            onCheckedChange={(v) => setRemember(Boolean(v))}
            disabled={loading}
          />
          Не спрашивать код на этом устройстве
        </label>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full" disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Подтвердить вход
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={loading}
      >
        Назад к вводу пароля
      </Button>
    </form>
  );
}
